import { apiFetch, ApiError } from './lib/api-client';

export { ApiError };

export interface ApiHelperErrorResponse<T = unknown> {
    data: T | null;
    status: number;
    error: true;
}

export function isApiHelperErrorResponse<T = unknown>(value: unknown): value is ApiHelperErrorResponse<T> {
    return typeof value === 'object'
        && value !== null
        && 'error' in value
        && (value as { error?: unknown }).error === true
        && 'status' in value;
}

function tryParseApiErrorBody(bodyText: unknown): unknown {
    if (typeof bodyText !== 'string') {
        return bodyText;
    }

    const trimmed = bodyText.trim();
    if (!trimmed) {
        return null;
    }

    try {
        return JSON.parse(trimmed) as unknown;
    } catch {
        return trimmed;
    }
}

function appendUnique(parts: string[], value: unknown): void {
    if (typeof value !== 'string') {
        return;
    }

    const trimmed = value.trim();
    if (!trimmed || parts.includes(trimmed)) {
        return;
    }

    parts.push(trimmed);
}

function buildApiErrorMessage(status: number, statusText: string, body: unknown): string {
    const parts = [`${status} ${statusText}`];

    if (body && typeof body === 'object' && !Array.isArray(body)) {
        const errorBody = body as Record<string, unknown>;
        const bodyMessage = typeof errorBody.message === 'string' ? errorBody.message.trim() : '';
        appendUnique(parts, bodyMessage);

        const exceptionType = typeof errorBody.exceptionType === 'string' ? errorBody.exceptionType.trim() : '';
        const exceptionMessage = typeof errorBody.exceptionMessage === 'string' ? errorBody.exceptionMessage.trim() : '';
        if (exceptionType || exceptionMessage) {
            const exceptionSummary = [exceptionType, exceptionMessage].filter(Boolean).join(': ');
            if (!bodyMessage || !bodyMessage.includes(exceptionSummary)) {
                appendUnique(parts, exceptionSummary);
            }
        }
    } else {
        appendUnique(parts, body);
    }

    return parts.join(' — ');
}

/**
 * Authenticated API call. Attaches a Bearer access token (from MSAL) and parses
 * JSON. Triggers a sign-in redirect on 401.
 *
 * @param {string} url
 * @param {string} [method]
 * @param {unknown} [data] - Request body (serialized as JSON for non-GET methods).
 * @param {boolean} [parseErrorResponse] - When true, returns
 *   `{ data, status, error }` for non-2xx responses instead of throwing.
 */
export async function ApiHelper<T = unknown>(url: string, method?: string, data?: unknown, parseErrorResponse?: false): Promise<T>;
export async function ApiHelper<T = unknown, E = unknown>(url: string, method: string | undefined, data: unknown, parseErrorResponse: true): Promise<T | ApiHelperErrorResponse<E>>;
export async function ApiHelper<T = unknown, E = unknown>(
    url: string,
    method: string = 'GET',
    data: unknown = {},
    parseErrorResponse = false,
): Promise<T | ApiHelperErrorResponse<E>> {
    const init: { method: string; body?: string } = { method: method ?? 'GET' };
    if (init.method !== 'GET' && data !== undefined && data !== null) {
        init.body = JSON.stringify(data);
    }

    let response;
    try {
        response = await apiFetch(url, init);
    } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
            // api-client already triggered an MSAL redirect; surface a clean message.
            throw new Error('Session expired. Please log in again.');
        }
        console.error('API Error:', error);
        throw error;
    }

    if (parseErrorResponse) {
        const responseData = await response.json().catch(() => null);
        if (response.ok) {
            return responseData as T;
        }
        return { data: responseData as E | null, status: response.status, error: true };
    }

    if (!response.ok) {
        const responseText = await response.text().catch(() => null);
        const body = tryParseApiErrorBody(responseText);
        throw new ApiError(
            response.status,
            body,
            buildApiErrorMessage(response.status, response.statusText, body),
        );
    }

    return await response.json() as T;
}
